/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import type Store from '@ember-data/store';
import type SalesforceInAppMessagingConfiguration from 'embercom/models/standalone/salesforce-in-app-messaging-configuration';

type NotificationsService = {
  notifyConfirmation(message: string): void;
  notifyResponseError(error: Error, options?: { default: string }): void;
};

interface Args {
  inAppMessagingConfig: SalesforceInAppMessagingConfiguration | undefined;
}

export default class StandaloneSalesforceSetupInAppMessagingConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: NotificationsService;
  @service declare intl: IntlService;
  @service declare store: Store;

  get activeConfiguration() {
    return this.finStandaloneService.salesforcePlatform.activeConfiguration;
  }

  get configurationData() {
    return this.finStandaloneService.salesforcePlatform.configurationData;
  }

  get salesforceInAppMessagingConfig() {
    return this.args.inAppMessagingConfig;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get isConnected(): boolean {
    return this.salesforceInAppMessagingConfig?.isConnected ?? false;
  }

  get isLive(): boolean {
    return this.salesforcePlatform.intercomMessengerConfig?.isMessengerEnabled ?? false;
  }

  get isFormValid(): boolean {
    if (
      !this.salesforceInAppMessagingConfig?.deploymentId ||
      !this.salesforceInAppMessagingConfig?.hostname
    ) {
      return false;
    }

    try {
      let parsedUrl = new URL(this.salesforceInAppMessagingConfig?.hostname);
      if (parsedUrl.protocol !== 'https:') {
        return false;
      }
      if (!parsedUrl.hostname.endsWith('.my.salesforce-scrt.com')) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  get isRevokeRunning(): boolean {
    return taskFor(this.revoke).isRunning;
  }

  get isConnectRunning(): boolean {
    return taskFor(this.connect).isRunning;
  }

  get embeddedServiceDeploymentsUrl() {
    return this.activeConfiguration!.buildLightningSetupUrl('EmbeddedServiceDeployments/home');
  }

  @restartableTask
  *connect(): TaskGenerator<void> {
    try {
      this.salesforceInAppMessagingConfig?.setProperties({
        hostname: this.salesforceInAppMessagingConfig?.hostname,
        deploymentId: this.salesforceInAppMessagingConfig?.deploymentId,
        organizationId: this.salesforcePlatform.salesforceConfig!.organisationId,
        salesforceApiIntegrationId: this.salesforcePlatform.salesforceConfig!.id,
      });
      yield this.salesforceInAppMessagingConfig?.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.save-success'),
      );
    } catch (error) {
      this.salesforceInAppMessagingConfig?.rollbackAttributes();
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.save-error'),
      });
    }
  }

  @dropTask
  *revoke(): TaskGenerator<void> {
    try {
      yield this.salesforceInAppMessagingConfig?.revoke();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.revoke-success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.in-app-messaging-connect.revoke-error'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::InAppMessaging::Connect': typeof StandaloneSalesforceSetupInAppMessagingConnect;
    'standalone/salesforce/setup/in-app-messaging/connect': typeof StandaloneSalesforceSetupInAppMessagingConnect;
  }
}
